import { Injectable, inject } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from "@angular/common/http";
import { Observable, from, throwError } from "rxjs";
import { switchMap, catchError, finalize } from "rxjs/operators";
import { AuthService } from "./auth.service";
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptorConfiguration
} from "@azure/msal-angular";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private msalInterceptorConfig = inject<MsalInterceptorConfiguration>(
    MSAL_INTERCEPTOR_CONFIG
  );

  // Track in-flight requests to prevent concurrent auth popups
  private pendingRequests = new Set<string>();

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Extract origin from request URL (e.g., http://localhost:8080)
    const urlParts = request.url.split("/");
    const origin = urlParts[0] + "//" + urlParts[2];
    const requestId = `${request.method}-${request.url}-${Date.now()}`;

    // Get protected resources map from config
    const protectedResourceMap =
      this.msalInterceptorConfig.protectedResourceMap;

    // Check if the request URL matches any protected resource
    const matchingScopes = Array.from(protectedResourceMap.entries()).find(
      ([endpoint]) => {
        // Strip trailing /* wildcard for origin-based matching
        const pattern = endpoint.replace(/\/\*$/, "");
        return request.url.toLowerCase().startsWith(pattern.toLowerCase());
      }
    );

    // If not protected resource, just pass through
    if (!matchingScopes) {
      return next.handle(request);
    }

    // Track this request
    this.pendingRequests.add(requestId);
    console.log(`[AuthInterceptor] Starting request ${requestId} to ${origin}`);

    // Get scopes needed for this endpoint
    const scopes = matchingScopes[1];
    const scopesArray = (Array.isArray(scopes) ? scopes : [scopes]).map(
      (scope) => (typeof scope === "string" ? scope : scope.toString())
    );

    console.log(
      `[AuthInterceptor] Getting token for scopes: ${scopesArray.join(", ")}`
    );

    // Add a timeout to prevent hanging requests. Must comfortably exceed one
    // silent-renewal attempt — MSAL's hidden-iframe fallback takes up to 10s
    // (DEFAULT_IFRAME_TIMEOUT_MS) — otherwise every request during a renewal
    // proceeds without a token and is a guaranteed 401.
    return from(
      Promise.race([
        this.authService.acquireToken(scopesArray),
        // If token acquisition takes too long, proceed without token
        new Promise<string | null>((resolve) =>
          setTimeout(() => {
            // console.log(`[AuthInterceptor] Token acquisition timed out for ${requestId}, proceeding without token`);
            resolve(null);
          }, 15000)
        )
      ])
    ).pipe(
      switchMap((token) => {
        console.log(
          `[AuthInterceptor] Token acquisition ${
            token ? "succeeded" : "failed"
          }`
        );

        if (!token) {
          // If we don't have a token, just proceed with the original request
          // console.log(`[AuthInterceptor] Proceeding with request ${requestId} without authentication token`);
          return next.handle(request);
        }

        // Add auth header
        const authRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });

        // Forward the authenticated request
        return next.handle(authRequest);
      }),
      catchError((error: HttpErrorResponse) => {
        console.log(`[AuthInterceptor] Error for ${requestId}:`, error);

        if (error.status === 401) {
          console.log(
            "[AuthInterceptor] 401 Unauthorized — propagating to caller"
          );
          // Recover from a dead session: store authenticated but no MSAL account.
          this.authService.handleUnauthorized();
        }

        return throwError(error);
      }),
      finalize(() => {
        // Clean up tracking
        this.pendingRequests.delete(requestId);
        console.log(
          `[AuthInterceptor] Completed request ${requestId}, ${this.pendingRequests.size} pending`
        );
      })
    );
  }
}
